import { Loader2 } from 'lucide-react';
import {
  useAdminScrapingSummary,
  useAdminScrapingErrors,
  useAdminScrapingFacilities,
  useAdminTriggerScrape,
} from '../../hooks/useAdminApi';

/** Convert a unix epoch (seconds) to a relative time string. */
function timeAgoEpoch(epoch: number | null): string {
  if (!epoch) return 'never';
  const ms = Date.now() - epoch * 1000;
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function isStale(epoch: number | null): boolean {
  if (!epoch) return true;
  const ms = Date.now() - epoch * 1000;
  return ms > 6 * 60 * 60 * 1000; // > 6 hours
}

export function AdminScrapingPage() {
  const summary = useAdminScrapingSummary();
  const errors = useAdminScrapingErrors({ limit: 10 });
  const facilities = useAdminScrapingFacilities({ limit: 10, sort: 'stalest' });
  const triggerScrape = useAdminTriggerScrape();

  if (summary.isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 text-[#8b949e] animate-spin" />
      </div>
    );
  }

  const sc = summary.data;
  const phases = sc?.phaseBreakdown ?? [];
  const totalScraped = phases.reduce((sum, p) => sum + p.cnt, 0);
  const totalFailed = phases.reduce((sum, p) => sum + p.failed, 0);
  const totalSucceeded = phases.reduce((sum, p) => sum + p.succeeded, 0);
  const avgDuration = sc?.avgDurationMs24h ?? 0;
  const stalestFacility = facilities.data?.facilities?.[0];

  // Facilities are already sorted by stalest from the backend when sort=stalest
  const sortedFacilities = facilities.data?.facilities ?? [];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-[22px] font-semibold text-white">Scraping</h1>
        <p className="text-[13px] text-[#8b949e]">Phase breakdown, errors, and facility status</p>
      </div>

      {/* 4-across stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-4">
          <div className="text-[#8b949e] text-[11px] uppercase tracking-wide mb-1">SCRAPED 1H</div>
          <div className="text-2xl font-bold text-emerald-500">{totalScraped}</div>
          <div className="text-[#8b949e] text-[11px] mt-0.5">total runs</div>
        </div>

        <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-4">
          <div className="text-[#8b949e] text-[11px] uppercase tracking-wide mb-1">FAILED 1H</div>
          <div className="text-2xl font-bold text-red-500">{totalFailed}</div>
          <div className="text-[#8b949e] text-[11px] mt-0.5">errors</div>
        </div>

        <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-4">
          <div className="text-[#8b949e] text-[11px] uppercase tracking-wide mb-1">AVG DURATION</div>
          <div className="text-2xl font-bold text-white">{(avgDuration / 1000).toFixed(1)}s</div>
          <div className="text-[#8b949e] text-[11px] mt-0.5">per scrape (24h)</div>
        </div>

        <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-4">
          <div className="text-[#8b949e] text-[11px] uppercase tracking-wide mb-1">STALEST</div>
          <div className="text-2xl font-bold text-amber-500">
            {stalestFacility?.lastScrapedAt ? timeAgoEpoch(stalestFacility.lastScrapedAt) : 'N/A'}
          </div>
          <div className="text-[#8b949e] text-[11px] mt-0.5 truncate">
            {stalestFacility?.name ?? 'no data'}
          </div>
        </div>
      </div>

      {/* Phase breakdown from backend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-5">
        {phases.map((phase) => (
          <div key={phase.source} className="bg-[#161b22] border border-[#21262d] rounded-lg p-4">
            <div className="text-[#8b949e] text-[11px] uppercase tracking-wide mb-3">
              {phase.source.toUpperCase()}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <div className="text-emerald-500 text-lg font-bold">{phase.succeeded}</div>
                <div className="text-[11px] text-[#8b949e]">succeeded</div>
              </div>
              <div>
                <div className="text-red-500 text-lg font-bold">{phase.failed}</div>
                <div className="text-[11px] text-[#8b949e]">failed</div>
              </div>
              <div>
                <div className="text-white text-lg font-bold">{phase.cnt}</div>
                <div className="text-[11px] text-[#8b949e]">total</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Scraping overview bar */}
      <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-4 mb-5">
        <div className="text-[#8b949e] text-[11px] uppercase tracking-wide mb-3">SCRAPES BREAKDOWN</div>
        <div className="flex items-end gap-1 h-[120px]">
          {(() => {
            const bars = [
              { label: 'Succeeded', success: totalSucceeded, failed: 0 },
              { label: 'Errors', success: 0, failed: totalFailed },
            ];
            const max = Math.max(...bars.map(b => b.success + b.failed), 1);
            return bars.map((d, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <div className="flex-1 flex flex-col justify-end gap-px w-full">
                  {d.success > 0 && (
                    <div
                      className="bg-emerald-500 rounded-t w-full"
                      style={{ height: `${(d.success / max) * 100}%` }}
                    />
                  )}
                  {d.failed > 0 && (
                    <div
                      className="bg-red-500 rounded-t w-full"
                      style={{ height: `${(d.failed / max) * 100}%` }}
                    />
                  )}
                </div>
                <div className="text-[10px] text-[#8b949e] mt-1">{d.label}</div>
              </div>
            ));
          })()}
        </div>
      </div>

      {/* Errors and Stalest side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-5">
        <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-4">
          <div className="text-[#8b949e] text-[11px] uppercase tracking-wide mb-3">RECENT ERRORS</div>
          <div className="space-y-2">
            {errors.isLoading ? (
              <div className="flex justify-center py-4">
                <Loader2 className="w-4 h-4 text-[#8b949e] animate-spin" />
              </div>
            ) : (errors.data?.errors ?? []).length === 0 ? (
              <div className="text-[13px] text-[#8b949e] py-4 text-center">No recent errors</div>
            ) : (
              (errors.data?.errors ?? []).map((err) => (
                <div
                  key={err.id}
                  className="flex items-center p-2.5 bg-[#1c2129] rounded-md border-l-[3px] border-red-500"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium text-white truncate">
                      {err.facilityName ?? 'Unknown facility'}
                    </div>
                    <div className="text-[11px] text-[#8b949e] truncate">{err.errorMessage}</div>
                  </div>
                  <div className="text-[11px] text-[#8b949e] mr-3 shrink-0">{timeAgoEpoch(err.scrapedAt)}</div>
                  {err.facilityId && (
                    <button
                      className="bg-emerald-500 text-white px-3 py-1 rounded text-[11px] font-medium hover:bg-emerald-600 disabled:opacity-50 shrink-0"
                      disabled={triggerScrape.isPending}
                      onClick={() => triggerScrape.mutate(err.facilityId!)}
                    >
                      Retry
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-4">
          <div className="text-[#8b949e] text-[11px] uppercase tracking-wide mb-3">STALEST FACILITIES</div>
          <div className="space-y-2">
            {facilities.isLoading ? (
              <div className="flex justify-center py-4">
                <Loader2 className="w-4 h-4 text-[#8b949e] animate-spin" />
              </div>
            ) : sortedFacilities.length === 0 ? (
              <div className="text-[13px] text-[#8b949e] py-4 text-center">No facilities</div>
            ) : (
              sortedFacilities.map((fac) => (
                <div
                  key={fac.id}
                  className={`flex items-center p-2.5 bg-[#1c2129] rounded-md border-l-[3px] ${
                    isStale(fac.lastScrapedAt) ? 'border-amber-500' : 'border-emerald-500'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium text-white truncate">{fac.name}</div>
                    <div className="text-[11px] text-[#8b949e]">{fac.originName ?? 'unknown'}</div>
                  </div>
                  <div className="text-[11px] text-[#8b949e] mr-3 shrink-0">
                    {fac.lastScrapedAt ? timeAgoEpoch(fac.lastScrapedAt) : 'never'}
                  </div>
                  <button
                    className="bg-emerald-500 text-white px-3 py-1 rounded text-[11px] font-medium hover:bg-emerald-600 disabled:opacity-50 shrink-0"
                    disabled={triggerScrape.isPending}
                    onClick={() => triggerScrape.mutate(fac.id)}
                  >
                    Scrape
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
